/**
 * Lever 1: dimensional and band audit.
 * Compares what each parcel was charged with what its actual size and weight
 * should cost, and finds parcels sitting just over a cheaper band.
 */
import {
  excessOver,
  findService,
  fitsBand,
  priceParcel,
  type Band,
  type RateCard,
} from "./ratecard.ts";
import type { Parcel, Pence } from "./types.ts";

export interface Lever1Options {
  /** A parcel within this many cm of a cheaper band's size limits counts as "near" (default 2) */
  nearBandCm?: number;
  /** ...and within this share of its weight limit (default 0.1 = 10%) */
  nearBandWeightShare?: number;
  /** Differences of this many pence or less are treated as matching (default 1) */
  tolerancePence?: number;
}

export type Lever1Flag =
  | "overcharged"
  | "undercharged"
  | "volumetric_applies"
  | "near_cheaper_band"
  | "no_band_fits"
  | "unknown_service";

export interface NearBand {
  bandCode: string;
  bandName: string;
  pricePence: Pence;
  /** Saving per parcel if it fitted this band */
  savingPence: Pence;
  overBy: { lengthCm?: number; widthCm?: number; heightCm?: number; weightKg?: number };
}

export interface Lever1ParcelResult {
  orderId: string;
  serviceCode: string;
  rateCard: string | null;
  actualWeightKg: number;
  volumetricWeightKg: number | null;
  chargeableWeightKg: number | null;
  bandCode: string | null;
  expectedPence: Pence | null;
  chargedPence: Pence;
  /** charged minus expected; positive means the client paid more than the rate card */
  differencePence: Pence | null;
  flags: Lever1Flag[];
  nearBand: NearBand | null;
}

export interface Lever1Summary {
  parcels: number;
  /** Parcels that could be priced against a rate card */
  priced: number;
  chargedPence: Pence;
  expectedPence: Pence;
  overchargedCount: number;
  overchargePence: Pence;
  underchargedCount: number;
  volumetricCount: number;
  nearBandCount: number;
  /** Saving if every near-band parcel moved into the cheaper band */
  nearBandSavingPence: Pence;
  noBandCount: number;
  unknownServiceCount: number;
  byBand: Record<string, { count: number; chargedPence: Pence; expectedPence: Pence }>;
}

function nearestCheaperBand(
  p: Parcel,
  chargeableKg: number,
  current: Band,
  bands: Band[],
  cm: number,
  share: number,
): NearBand | null {
  let best: NearBand | null = null;
  for (const band of bands) {
    if (band.pricePence >= current.pricePence) continue;
    if (fitsBand(p.dims, chargeableKg, band)) continue;
    const over = excessOver(p.dims, chargeableKg, band);
    const sizeOk = [over.lengthCm, over.widthCm, over.heightCm].every((x) => x === undefined || x <= cm);
    const weightOk = over.weightKg === undefined || over.weightKg <= band.maxWeightKg * share;
    if (!sizeOk || !weightOk) continue;
    const savingPence = current.pricePence - band.pricePence;
    if (!best || savingPence > best.savingPence) {
      best = { bandCode: band.code, bandName: band.name, pricePence: band.pricePence, savingPence, overBy: over };
    }
  }
  return best;
}

export function auditParcel(p: Parcel, cards: RateCard[], opts: Lever1Options = {}): Lever1ParcelResult {
  const cm = opts.nearBandCm ?? 2;
  const share = opts.nearBandWeightShare ?? 0.1;
  const tol = opts.tolerancePence ?? 1;

  const base: Lever1ParcelResult = {
    orderId: p.orderId,
    serviceCode: p.carrierService,
    rateCard: null,
    actualWeightKg: p.actualWeightKg,
    volumetricWeightKg: null,
    chargeableWeightKg: null,
    bandCode: null,
    expectedPence: null,
    chargedPence: p.chargedPence,
    differencePence: null,
    flags: [],
    nearBand: null,
  };

  const found = findService(cards, p.carrierService);
  if (!found) return { ...base, flags: ["unknown_service"] };
  const { card, service } = found;
  base.rateCard = `${card.id}@${card.version}`;

  const priced = priceParcel(p.dims, p.actualWeightKg, service);
  if (!priced) return { ...base, flags: ["no_band_fits"] };

  const flags: Lever1Flag[] = [];
  if (priced.volumetricKg !== null && priced.volumetricKg > p.actualWeightKg) flags.push("volumetric_applies");
  const diff = p.chargedPence - priced.band.pricePence;
  if (diff > tol) flags.push("overcharged");
  if (diff < -tol) flags.push("undercharged");
  const near = nearestCheaperBand(p, priced.chargeableKg, priced.band, service.bands, cm, share);
  if (near) flags.push("near_cheaper_band");

  return {
    ...base,
    volumetricWeightKg: priced.volumetricKg,
    chargeableWeightKg: priced.chargeableKg,
    bandCode: priced.band.code,
    expectedPence: priced.band.pricePence,
    differencePence: diff,
    flags,
    nearBand: near,
  };
}

export function runLever1(parcels: Parcel[], cards: RateCard[], opts: Lever1Options = {}) {
  const results = parcels.map((p) => auditParcel(p, cards, opts));
  const summary: Lever1Summary = {
    parcels: results.length,
    priced: 0,
    chargedPence: 0,
    expectedPence: 0,
    overchargedCount: 0,
    overchargePence: 0,
    underchargedCount: 0,
    volumetricCount: 0,
    nearBandCount: 0,
    nearBandSavingPence: 0,
    noBandCount: 0,
    unknownServiceCount: 0,
    byBand: {},
  };
  for (const r of results) {
    summary.chargedPence += r.chargedPence;
    if (r.flags.includes("unknown_service")) summary.unknownServiceCount++;
    if (r.flags.includes("no_band_fits")) summary.noBandCount++;
    if (r.expectedPence === null || r.bandCode === null) continue;
    summary.priced++;
    summary.expectedPence += r.expectedPence;
    const key = `${r.serviceCode}/${r.bandCode}`;
    const b = (summary.byBand[key] ??= { count: 0, chargedPence: 0, expectedPence: 0 });
    b.count++;
    b.chargedPence += r.chargedPence;
    b.expectedPence += r.expectedPence;
    if (r.flags.includes("overcharged")) {
      summary.overchargedCount++;
      summary.overchargePence += r.differencePence!;
    }
    if (r.flags.includes("undercharged")) summary.underchargedCount++;
    if (r.flags.includes("volumetric_applies")) summary.volumetricCount++;
    if (r.nearBand) {
      summary.nearBandCount++;
      summary.nearBandSavingPence += r.nearBand.savingPence;
    }
  }
  return { results, summary };
}
